<template>
  <div class="order-book-table-wrapper">
    <b-table id="order-book-table" striped hover :items="table.items" :fields="table.fields" show-empty>
      <template slot="empty">

        <div v-if="isLoading">
          <table-loader/>
        </div>

        <div v-else>
          Unfortunately there has been no trades yet.
        </div>

      </template>

      <template #cell(buyComparison)="data">
        <b-progress :value="data.value"></b-progress>
      </template>

      <template #cell(sellComparison)="data">
        <b-progress :value="data.value"></b-progress>
      </template>
    </b-table>

    <last-updated :date="lastUpdated"/>

  </div>
</template>

<script>
  // Api
  import OrderBookApi from "@/services/order-book";

  // Mixins
  import {mixinDatesHandler} from '@/mixins/dates-handler';
  import {mixinAmountsHandler} from '@/mixins/amounts-handler';

  // Components
  import TableLoader from '@/components/ui/loaders/TableLoader';
  import LastUpdated from '@/components/ui/table/LastUpdatedDate';

  export default {
    name: 'SharesOrderBook',

    mixins: [mixinDatesHandler, mixinAmountsHandler],

    components: { TableLoader, LastUpdated },

    data() {
      return {
        isLoading: false,

        table: {
          fields: [
            {
              key: 'buy',
              label: 'Buy',
              sortable: false
            },
            {
              key: 'buyVolume',
              label: 'Volume',
              sortable: false
            },
            {
              key: 'buyComparison',
              label: '',
              sortable: false
            },
            {
              key: 'sellComparison',
              label: '',
              sortable: false
            },
            {
              key: 'sellVolume',
              label: 'Volume',
              sortable: false
            },
            {
              key: 'sell',
              label: 'Sell',
              sortable: false
            },
          ],
          items: [],
        },

        data: {},
      }
    },

    methods: {
      startLoader() {
        this.isLoading = true;
      },

      stopLoader() {
        this.isLoading = false;
      },

      fetchOrderBookData() {
        return new Promise((resolve) => {
          new OrderBookApi({}, {urlExtension: 'Items'}).get(function (response) {
            if (response.status !== 200) {
              return resolve([]);
            }

            return resolve(response.data);
          });
        });
      },

      setOrderBookData(orderBookData) {
        return new Promise((resolve) => {
          const instrument     = orderBookData.Instruments[0];
          const orderBookItems = instrument.OrderBookItems;

          let items = [];
          let max   = 0;

          orderBookItems.forEach((x) => {
            max = Math.max(max, x.BidPrice * x.BidQuantity, x.AskPrice * x.AskQuantity);
          });

          orderBookItems.forEach((x) => {
            const buyComparison  = (100 * x.BidPrice * x.BidQuantity / max);
            const sellComparison = (100 * x.AskPrice * x.AskQuantity / max);

            items.push({
              buy: this.formatAmount(x.BidPrice, 2),
              buyVolume: this.formatAmount(x.BidQuantity, 0),
              buyComparison: buyComparison,
              sell: this.formatAmount(x.AskPrice, 2),
              sellVolume: this.formatAmount(x.AskQuantity, 0),
              sellComparison: sellComparison,
            });
          });

          resolve(items);
        });
      },

      async fetchData() {
        this.startLoader();

        let data = await this.fetchOrderBookData();

        this.data = data;

        if ('Instruments' in data && data.Instruments.length > 0) {
          this.table.items = await this.setOrderBookData(data);
        }

        this.stopLoader();
      },

      formatAmount(amount, decimals) {
        if (decimals && decimals > 0) {
          return this._mixinAmountsHandler_formatAmount(amount, decimals);
        }

        return this._mixinAmountsHandler_formatAmount(amount, 0);
      },
    },

    computed: {
      lastUpdated() {
        const data = this.data;

        if (data.Instruments && data.Instruments.length > 0) {
          return data.Instruments[0].LastUpdate;
        }

        return null;
      }
    },

    mounted: function () {
      this.$nextTick(function () {
        this.fetchData();
      });
    },
  }
</script>

<style scoped lang="scss">
  .order-book-table-wrapper {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }

  ::v-deep #order-book-table {
    tr td:nth-child(3)  {
      .progress-bar {
        background-color: #2095f2;
      }

      .progress {
        flex-direction: row-reverse;
        background-color: #dedede;
      }
    }

    tr td:nth-child(4)  {
      .progress-bar {
        background-color: #8ac249;
      }

      .progress {
        background-color: #dedede;
      }
    }

    tr td:nth-child(3), tr td:nth-child(4) {
      width: 250px;
      min-width: 150px;
    }

    tr:not(.b-table-empty-row ) td:nth-child(1), tr td:nth-child(2), tr td:nth-child(5), tr td:nth-child(6) {
      text-align: right;
    }

    tr th:nth-child(2), tr th:nth-child(5), tr th:nth-child(6) {
      text-align: right;
    }

    tr td {
      vertical-align: middle;
    }

    .b-table-empty-row {
      text-align: center;
    }
  }
</style>
